import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  AlertSeverityEnum,
  Ipv4,
  Ipv4PagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';


const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchIpv4 = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetIpv4Params> = {}) => {
  return useQuery({
    queryKey: ['ipv4-red', ...Object.values(params ?? {})],
    queryFn: () => getAllIpv4(params),
    enabled: enabled,
  });
};

export const useGetIpv4 = (id: string) => {
  return useQuery({
    queryKey: ['ipv4-red', id],
    queryFn: () => getIpv4(id),
  });
};

export const useCreateIpv4 = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createIpv4,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ipv4 creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateIpv4 = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateIpv4,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4-red'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ipv4 actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteIpv4 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteIpv4,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipv4-red'] });
    },
    onError: () => { },
  });
};

///* axios
export type GetIpv4Params = Partial<Ipv4> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
  random?: boolean;
};

export type CreateIpv4Params = Omit<Ipv4, 'id_ipv4'>;
export type UpdateIpv4Params = {
  id: string;
  data: Partial<CreateIpv4Params>;
};

export const getAllIpv4 = async (params?: GetIpv4Params) => {
  const stateParams = { ...params };

  // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<Ipv4PagingRes>(`${urlBackend}api/v1/ipv4/?${queryParams}`, true);
};

export const getIpv4 = async (id: string) => {
  try {
    return await get<Ipv4>(`${urlBackend}api/v1/ipv4/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createIpv4 = async (data: CreateIpv4Params) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Ipv4>(`${urlBackend}api/v1/ipv4/`, data, true);
};

export const updateIpv4 = async ({ id, data }: UpdateIpv4Params) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Ipv4>(`${urlBackend}api/v1/ipv4/${id}/`, data, true);
};

export const deleteIpv4 = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;

  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Ipv4>(`${urlBackend}api/v1/ipv4/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
